import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { NAV, LOCATIONS, SOCIALS, IZI } from '../data/site';
import { useScrollY, useBodyLock } from '../hooks/useReveal';
import { ArrowRight, Bag, Close, Phone, SOCIAL_ICONS } from './Icons';
import logoInk from '../assets/logo-ink.png';
import logoCream from '../assets/logo-cream.png';
import './Nav.css';

export default function Nav() {
  const { pathname } = useLocation();
  const y = useScrollY();
  const [open, setOpen] = useState(false);

  // Hysteresis: condense past 56px, release only above 8px — a single
  // threshold made the bar flicker when tiny scroll jitters crossed it.
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    setCondensed((c) => (c ? y > 8 : y > 56));
  }, [y]);

  // Reading progress for the current page — the nav doubles as the indicator
  // of where you are, which matters now that pages flow into each other.
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    setProgress(scrollable > 0 ? Math.min(1, Math.max(0, y / scrollable)) : 0);
  }, [y, pathname]);
  useBodyLock(open);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className={`nav${condensed ? ' is-condensed' : ''}${open ? ' is-open' : ''}`}>
        <div className="nav__inner page">
          <Link to="/" className="nav__brand" aria-label="The Local — home">
            <img className="nav__logo nav__logo--ink" src={logoInk} alt="" width="140" height="104" />
            <img className="nav__logo nav__logo--cream" src={logoCream} alt="" width="140" height="104" />
            <span className="sr-only">The Local</span>
          </Link>

          <nav className="nav__links" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav__actions">
            <a
              className="nav__order"
              href={IZI.web}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Bag size={15} />
              <span>Order</span>
            </a>
            <Link to="/bookings" className="btn btn--sm nav__book">
              <span>Book a table</span>
            </Link>
            <button
              type="button"
              className={`nav__burger${open ? ' is-open' : ''}`}
              aria-expanded={open}
              aria-controls="site-menu"
              onClick={() => setOpen((v) => !v)}
            >
              <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
              <span className="nav__burger-line" />
              <span className="nav__burger-line" />
            </button>
          </div>
        </div>
        <span
          className="nav__progress"
          style={{ transform: `scaleX(${progress})` }}
          aria-hidden="true"
        />
      </header>

      <div id="site-menu" className={`menu${open ? ' is-open' : ''}`} hidden={!open}>
        <div className="menu__inner page">
          <button type="button" className="menu__close" onClick={() => setOpen(false)}>
            <Close />
            <span className="sr-only">Close menu</span>
          </button>

          <nav className="menu__nav" aria-label="Mobile">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `menu__link display d2${isActive ? ' is-active' : ''}`}
                style={{ transitionDelay: `${0.06 * i + 0.1}s` }}
              >
                <span className="menu__index tabular">0{i + 1}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="menu__foot">
            <div className="menu__col">
              <p className="eyebrow is-plain">Call us</p>
              {LOCATIONS.map((loc) => (
                <a key={loc.id} className="menu__contact" href={`tel:${loc.phoneIntl}`}>
                  <Phone size={14} />
                  <span>
                    {loc.name} <span className="tabular">{loc.phone}</span>
                  </span>
                </a>
              ))}
            </div>
            <div className="menu__col">
              <p className="eyebrow is-plain">Follow</p>
              <div className="menu__socials">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICONS[s.id];
                  return (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
            <a className="btn btn--gold menu__cta" href={IZI.web} target="_blank" rel="noopener noreferrer">
              <span>Order on {IZI.brand}</span>
              <ArrowRight />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
